import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CreateProductionPlanUi from "./CreateProductionPlanUi";
import { FetchProductStockByIdApi } from "../../apis/product.api";
import { callApiAction } from "../../store/actions/commonAction";
import { useDispatch } from "react-redux";
import { callSnackBar } from "../../store/actions/snackbarAction";
import {
  MATERIAL_STAGES,
  MATERIAL_TYPE,
  PRODUCTION_PLANNING_STATUS,
  SNACK_BAR_VARIETNS,
} from "../../utils/constants";
import { closeModal } from "../../store/actions/modalAction";
import {
  CreateAssemblyStockOut,
  createProductionPlanningApi,
  FetchAvailableStockInAssemblyApi,
  GetProductionPlanningByIdApi,
  ProductionPlanningStockOutApi,
  UpdateAssemblyFgStatus,
  updateProductionPlanningApi,
} from "../../apis/ProductionPlanning.api";
import { FetchInventoryOutLogApi } from "../../apis/inventoryLogs.api";
import moment from "moment";

const CreateProductionPlanController = ({
  id,
  quantity,
  callBack,
  isview,
  isEdit = false, // New prop for edit mode
  params,
}) => {
  const [loading, setLoading] = useState(Boolean(id && (isview || isEdit)));
  const [fields, setFields] = useState({});
  const [fetchFields, setFetchFields] = useState({
    sfgs: [],
    skds: [],
    rawMaterials: [],
  });
  const [recordingProduction, setRecordingProduction] = useState(false);
  const [progressEntryQty, setProgressEntryQty] = useState("");
  const [fgStatus, setFgStatus] = useState("");
  const [producedFgQty, setProducedFgQty] = useState(0);
  const [fgStatusSummary, setFgStatusSummary] = useState({});
  const [assemblyStockMap, setAssemblyStockMap] = useState({});
  const [reworkableToPassedQty, setReworkableToPassedQty] = useState("");
  const [convertingStatus, setConvertingStatus] = useState(false);
  const [reworkableToRejectedQty, setReworkableToRejectedQty] = useState("");
  const [convertingToRejectedStatus, setConvertingToRejectedStatus] =
    useState(false);
  const dispatch = useDispatch();
  const hasFetched = useRef(false);
  const totalQuantity = isview || isEdit ? fields?.quantity : quantity;
  const isProductionStartedView =
    (isview || isEdit) &&
    [
      PRODUCTION_PLANNING_STATUS.PRODUCTION_STARTED,
      PRODUCTION_PLANNING_STATUS.PRODUCTION_COMPLETED,
    ].includes(params?.status);

  const getNumber = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const fetchAssemblyStockSummary = useCallback(
    (productionPlanId) => {
      if (!productionPlanId) return;

      dispatch(
        callApiAction(
          async () =>
            await FetchAvailableStockInAssemblyApi({
              productionPlan: productionPlanId,
            }),
          (response) => {
            const apiData = response?.data ? response.data : response;

            const buildFgStatusSummary = (groupedByMaterialType = []) => {
              const fgGroup = (groupedByMaterialType || []).find(
                (group) => Number(group?.materialType) === MATERIAL_TYPE.FG,
              );

              const summary = {
                passedQty: 0,
                reworkableQty: 0,
                rejectedQty: 0,
                totalProducedQty: 0,
              };

              (fgGroup?.materials || []).forEach((material) => {
                (material?.fgStatusWiseQuantity || []).forEach((statusItem) => {
                  const quantity = getNumber(statusItem?.quantity);
                  summary.totalProducedQty += quantity;

                  switch (Number(statusItem?.fgStatus)) {
                    case 1:
                      summary.reworkableQty += quantity;
                      break;
                    case 2:
                      summary.rejectedQty += quantity;
                      break;
                    case 3:
                      summary.passedQty += quantity;
                      break;
                    default:
                      break;
                  }
                });
              });

              return summary;
            };

            const nextFgStatusSummary =
              apiData?.fgStatusSummary ||
              buildFgStatusSummary(apiData?.groupedByMaterialType || []);
            setFgStatusSummary(nextFgStatusSummary);

            const totalProducedFromStatuses =
              getNumber(nextFgStatusSummary?.passedQty) +
              getNumber(nextFgStatusSummary?.reworkableQty) +
              getNumber(nextFgStatusSummary?.rejectedQty);

            setProducedFgQty(
              getNumber(
                apiData?.noOfQuantityCompleted ??
                  nextFgStatusSummary?.totalProducedQty ??
                  totalProducedFromStatuses,
              ),
            );

            const nextStockMap = {};
            (apiData?.groupedByMaterialType || []).forEach((group) => {
              const materialType = group?.materialType;
              (group?.materials || []).forEach((material) => {
                const materialId = String(material?.materialId || "");
                if (!materialId) return;
                nextStockMap[`${materialType}:${materialId}`] = getNumber(
                  material?.availableQuantity,
                );
              });
            });

            setAssemblyStockMap(nextStockMap);
          },
          () => {
            setAssemblyStockMap({});
          },
        ),
      );
    },
    [dispatch],
  );

  const productionProgressSummary = useMemo(() => {
    const plannedFgQty = getNumber(totalQuantity);
    const normalizedProducedFgQty = Math.min(
      Math.max(getNumber(producedFgQty), 0),
      plannedFgQty,
    );
    const remainingFgQty = Math.max(plannedFgQty - normalizedProducedFgQty, 0);

    const buildMaterialSummary = (items = [], key, materialType) =>
      items.map((item) => {
        const materialId = String(item?.[key] || item?._id || "");
        const materialPerFg = getNumber(item?.min_of_quantity);
        const totalRequiredQty = materialPerFg * plannedFgQty;
        const consumedQty = materialPerFg * normalizedProducedFgQty;
        const remainingQty = Math.max(totalRequiredQty - consumedQty, 0);
        const assemblyAvailableQty = getNumber(
          assemblyStockMap[`${materialType}:${materialId}`],
        );

        return {
          id: materialId,
          name: item?.name || "NA",
          totalRequiredQty,
          consumedQty,
          remainingQty,
          assemblyAvailableQty,
        };
      });

    return {
      plannedFgQty,
      producedFgQty: normalizedProducedFgQty,
      remainingFgQty,
      groups: [
        {
          key: "sfgs",
          title: "Semi Finished Good",
          items: buildMaterialSummary(fields?.sfgs, "sfgId", MATERIAL_TYPE.SFG),
        },
        {
          key: "skds",
          title: "Semi Knocked Down",
          items: buildMaterialSummary(fields?.skds, "skdId", MATERIAL_TYPE.SKD),
        },
        {
          key: "rawMaterials",
          title: "Raw Materials",
          items: buildMaterialSummary(
            fields?.rawMaterials,
            "rawMaterialId",
            MATERIAL_TYPE.RAW_MATERIALS,
          ),
        },
      ],
    };
  }, [assemblyStockMap, fields, producedFgQty, totalQuantity]);

  // Check if all materials are available for production start
  const canStartProduction = useMemo(() => {
    if (!isEdit) return false;

    // Helper function to check if materials are available and have sufficient stock
    const checkMaterialAvailability = (materials) => {
      if (!materials || materials.length === 0) return true; // No materials needed

      return materials.every((item) => {
        const hasGoodStatus =
          item.status === MATERIAL_STAGES.AVAILABLE ||
          item.status === MATERIAL_STAGES.RECEIVED;
        const hasSufficientStock = item.difference >= 0;

        console.log(`Material ${item.name}:`, {
          status: item.status,
          difference: item.difference,
          hasGoodStatus,
          hasSufficientStock,
        });

        return hasGoodStatus && hasSufficientStock;
      });
    };

    const allSfgsAvailable = checkMaterialAvailability(fields?.sfgs);
    const allSkdsAvailable = checkMaterialAvailability(fields?.skds);
    const allRawMaterialsAvailable = checkMaterialAvailability(
      fields?.rawMaterials,
    );

    const canStart =
      allSfgsAvailable && allSkdsAvailable && allRawMaterialsAvailable;

    console.log("Production start check:", {
      allSfgsAvailable,
      allSkdsAvailable,
      allRawMaterialsAvailable,
      canStart,
    });

    return canStart;
  }, [fields, isEdit]);

  const createFunction = async (e) => {
    e.preventDefault();
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await createProductionPlanningApi(fields),
        async () => {
          callBack();
          setLoading(false);
          dispatch(
            callSnackBar(
              "Created Production Planning Successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
          dispatch(closeModal("Production-Planning"));
        },
        (err) => {
          setLoading(false);
          setFields({ ...fields, err });
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const fetchById = useCallback(
    (id) => {
      setLoading(true);
      dispatch(
        callApiAction(
          async () =>
            isview || isEdit
              ? await GetProductionPlanningByIdApi({ id })
              : await FetchProductStockByIdApi({ id }),
          async (response) => {
            const fetchQuantity =
              isview || isEdit ? response?.quantity : quantity;
            if (isview || isEdit) {
              fetchAssemblyStockSummary(id);
            }
            const GetStatus = (item) => {
              return (item?.currentStock || 0) -
                item?.min_of_quantity * (fetchQuantity || 0) <
                0
                ? MATERIAL_STAGES.PURCHASE
                : MATERIAL_STAGES.AVAILABLE;
            };
            const Getsfgsdata = (item1, response1) => {
              const item = response1?.sfgs?.find(
                (item) =>
                  item1 == (item?.sfgId?._id ? item?.sfgId?._id : item._id),
              );
              return {
                sfgId: item?.sfgId?._id ? item?.sfgId?._id : item._id,
                _id: item?.sfgId?._id ? item?.sfgId?._id : item._id,
                min_of_quantity: item.min_of_quantity,
                currentStock: item.currentStock,
                name: item?.sfgId?.name ? item?.sfgId?.name : item.name,
                difference:
                  (item?.currentStock || 0) -
                  item?.min_of_quantity * (fetchQuantity || 0),
                vendor_name: item?.vendor?.name ?? null,
                vendor: item?.vendor?._id ?? null,
                lead_time: Number(item?.lead_time ?? 0)
                  ? Number(item?.lead_time ?? 0)
                  : Number(item?.vendor?.sfg?.lead_time ?? 0),
                status: GetStatus(item),
              };
            };
            const Getskdsdata = (item1, response1) => {
              const item = response1?.skds?.find(
                (item) =>
                  item1 === (item?.skdId?._id ? item?.skdId?._id : item._id),
              );
              return {
                skdId: item?.skdId?._id ? item?.skdId?._id : item._id,
                _id: item?.skdId?._id ? item?.skdId?._id : item._id,
                min_of_quantity: item.min_of_quantity,
                currentStock: item.currentStock,
                name: item?.skdId?.name ? item?.skdId?.name : item.name,
                difference:
                  (item?.currentStock || 0) -
                  item?.min_of_quantity * (fetchQuantity || 0),
                vendor_name: item?.vendor?.name ?? null,
                vendor: item?.vendor?._id ?? null,
                lead_time: Number(item?.lead_time ?? 0)
                  ? Number(item?.lead_time ?? 0)
                  : Number(item?.vendor?.skd?.lead_time ?? 0),
                status: GetStatus(item),
              };
            };
            const GetrawMaterialsdata = (item1, response1) => {
              const item = response1.rawMaterials.find(
                (item) =>
                  item1 ==
                  (item?.rawMaterialId?._id
                    ? item?.rawMaterialId?._id
                    : item._id),
              );
              console.log("first item", item);
              return {
                rawMaterialId: item?.rawMaterialId?._id
                  ? item?.rawMaterialId?._id
                  : item._id,
                _id: item?.rawMaterialId?._id
                  ? item?.rawMaterialId?._id
                  : item._id,
                min_of_quantity: item.min_of_quantity,
                currentStock: item.currentStock,
                name: item?.rawMaterialId?.name
                  ? item?.rawMaterialId?.name
                  : item.name,
                difference:
                  (item?.currentStock || 0) -
                  item?.min_of_quantity * (fetchQuantity || 0),
                vendor_name: item?.vendor?.name ?? null,
                vendor: item?.vendor?._id ?? null,
                lead_time: Number(item?.lead_time ?? 0)
                  ? Number(item?.lead_time ?? 0)
                  : Number(item?.vendor?.rawMaterial?.lead_time ?? 0),
                status: GetStatus(item),
              };
            };
            if (isview || isEdit) {
              dispatch(
                callApiAction(
                  async () =>
                    await FetchProductStockByIdApi({ id: params.fg_id }),
                  async (response1) => {
                    setFields((prev) => ({
                      fg_id: !isview || isEdit ? id : response?.fg_id,
                      name: response?.name,
                      quantity: fetchQuantity,
                      fg_Code: response?.fg_Code,
                      sfgs: response?.sfgs?.map((item) =>
                        GetStatus(item) === MATERIAL_STAGES.PURCHASE
                          ? Getsfgsdata(
                              item?.sfgId?._id ? item?.sfgId?._id : item._id,
                              response1,
                            )
                          : {
                              sfgId: item?.sfgId?._id
                                ? item?.sfgId?._id
                                : item._id,
                              _id: item?.sfgId?._id
                                ? item?.sfgId?._id
                                : item._id,
                              min_of_quantity: item.min_of_quantity,
                              currentStock: item.currentStock,
                              name: item?.sfgId?.name
                                ? item?.sfgId?.name
                                : item.name,
                              difference:
                                (item?.currentStock || 0) -
                                item?.min_of_quantity * (fetchQuantity || 0),
                              vendor_name: item?.vendor?.name ?? null,
                              vendor: item?.vendor?._id ?? null,
                              lead_time: Number(item?.lead_time ?? 0)
                                ? Number(item?.lead_time ?? 0)
                                : Number(item?.vendor?.sfg?.lead_time ?? 0),
                              status:
                                isview || isEdit
                                  ? GetStatus(item) == MATERIAL_STAGES.AVAILABLE
                                    ? MATERIAL_STAGES.RECEIVED
                                    : GetStatus(item)
                                  : GetStatus(item),
                            },
                      ),
                      skds: response?.skds?.map((item) =>
                        GetStatus(item) === MATERIAL_STAGES.PURCHASE
                          ? Getskdsdata(
                              item?.skdId?._id ? item?.skdId?._id : item._id,
                              response1,
                            )
                          : {
                              skdId: item?.skdId?._id
                                ? item?.skdId?._id
                                : item._id,
                              _id: item?.skdId?._id
                                ? item?.skdId?._id
                                : item._id,
                              min_of_quantity: item.min_of_quantity,
                              currentStock: item.currentStock,
                              name: item?.skdId?.name
                                ? item?.skdId?.name
                                : item.name,
                              difference:
                                (item?.currentStock || 0) -
                                item?.min_of_quantity * (fetchQuantity || 0),
                              vendor_name: item?.vendor?.name ?? null,
                              vendor: item?.vendor?._id ?? null,
                              lead_time: Number(item?.lead_time ?? 0)
                                ? Number(item?.lead_time ?? 0)
                                : Number(item?.vendor?.skd?.lead_time ?? 0),
                              status:
                                isview || isEdit
                                  ? GetStatus(item) == MATERIAL_STAGES.AVAILABLE
                                    ? MATERIAL_STAGES.RECEIVED
                                    : GetStatus(item)
                                  : GetStatus(item),
                            },
                      ),
                      rawMaterials: response?.rawMaterials?.map((item) =>
                        GetStatus(item) === MATERIAL_STAGES.PURCHASE
                          ? GetrawMaterialsdata(
                              item?.rawMaterialId?._id
                                ? item?.rawMaterialId?._id
                                : item._id,
                              response1,
                            )
                          : {
                              rawMaterialId: item?.rawMaterialId?._id
                                ? item?.rawMaterialId?._id
                                : item._id,
                              _id: item?.rawMaterialId?._id
                                ? item?.rawMaterialId?._id
                                : item._id,
                              min_of_quantity: item.min_of_quantity,
                              currentStock: item.currentStock,
                              name: item?.rawMaterialId?.name
                                ? item?.rawMaterialId?.name
                                : item.name,
                              difference:
                                (item?.currentStock || 0) -
                                item?.min_of_quantity * (fetchQuantity || 0),
                              vendor_name: item?.vendor?.name ?? null,
                              vendor: item?.vendor?._id ?? null,
                              lead_time: Number(item?.lead_time ?? 0)
                                ? Number(item?.lead_time ?? 0)
                                : Number(
                                    item?.vendor?.rawMaterial?.lead_time ?? 0,
                                  ),
                              status:
                                isview || isEdit
                                  ? GetStatus(item) == MATERIAL_STAGES.AVAILABLE
                                    ? MATERIAL_STAGES.RECEIVED
                                    : GetStatus(item)
                                  : GetStatus(item),
                            },
                      ),
                    }));
                    setLoading(false);
                  },
                  (err) => {
                    setFields((prev) => ({ ...prev, err }));
                    setLoading(false);
                  },
                ),
              );
            } else {
              setFields((prev) => ({
                fg_id: !isview && !isEdit ? id : response?.fg_id,
                name: response?.name,
                quantity: fetchQuantity,
                fg_Code: response?.fg_Code,
                sfgs: response?.sfgs?.map((item) => ({
                  sfgId: item?.sfgId?._id ? item?.sfgId?._id : item._id,
                  _id: item?.sfgId?._id ? item?.sfgId?._id : item._id,
                  min_of_quantity: item.min_of_quantity,
                  currentStock: item.currentStock,
                  name: item?.sfgId?.name ? item?.sfgId?.name : item.name,
                  difference:
                    (item?.currentStock || 0) -
                    item?.min_of_quantity * (fetchQuantity || 0),
                  vendor_name: item?.vendor?.name ?? null,
                  vendor: item?.vendor?._id ?? null,
                  lead_time: Number(item?.lead_time ?? 0)
                    ? Number(item?.lead_time ?? 0)
                    : Number(item?.vendor?.sfg?.lead_time ?? 0),
                  status: GetStatus(item),
                })),
                skds: response?.skds?.map((item) => ({
                  skdId: item?.skdId?._id ? item?.skdId?._id : item._id,
                  _id: item?.skdId?._id ? item?.skdId?._id : item._id,
                  min_of_quantity: item.min_of_quantity,
                  currentStock: item.currentStock,
                  name: item?.skdId?.name ? item?.skdId?.name : item.name,
                  difference:
                    (item?.currentStock || 0) -
                    item?.min_of_quantity * (fetchQuantity || 0),
                  vendor_name: item?.vendor?.name ?? null,
                  vendor: item?.vendor?._id ?? null,
                  lead_time: Number(item?.lead_time ?? 0)
                    ? Number(item?.lead_time ?? 0)
                    : Number(item?.vendor?.skd?.lead_time ?? 0),
                  status: GetStatus(item),
                })),
                rawMaterials: response?.rawMaterials?.map((item) => ({
                  rawMaterialId: item?.rawMaterialId?._id
                    ? item?.rawMaterialId?._id
                    : item._id,
                  _id: item?.rawMaterialId?._id
                    ? item?.rawMaterialId?._id
                    : item._id,
                  min_of_quantity: item.min_of_quantity,
                  currentStock: item.currentStock,
                  name: item?.rawMaterialId?.name
                    ? item?.rawMaterialId?.name
                    : item.name,
                  difference:
                    (item?.currentStock || 0) -
                    item?.min_of_quantity * (fetchQuantity || 0),
                  vendor_name: item?.vendor?.name ?? null,
                  vendor: item?.vendor?._id ?? null,
                  lead_time: Number(item?.lead_time ?? 0)
                    ? Number(item?.lead_time ?? 0)
                    : Number(item?.vendor?.rawMaterial?.lead_time ?? 0),
                  status: GetStatus(item),
                })),
              }));
              setLoading(false);
            }
          },
          (err) => {
            setFields((prev) => ({ ...prev, err }));
            setLoading(false);
          },
        ),
      );
    },
    [dispatch, fetchAssemblyStockSummary, id, isview, isEdit, quantity],
  );

  const fetchList = (date, materialId, quantity, materialType, type) => {
    // setLoading(true);
    dispatch(
      callApiAction(
        async () =>
          await FetchInventoryOutLogApi({
            date: date,
            quantity: quantity,
            materialId: materialId,
            materialType: materialType,
          }),
        (response) => {
          if (response) {
            setFetchFields((prev) => ({
              ...prev,
              [type]: [
                ...(prev[type] || []),
                {
                  batchNumber: response.batchNumber,
                  materialId: response.materialId,
                  totalPrice: response?.totalPrice,
                  materialType,
                  date,
                  quantity,
                },
              ],
            }));
          }
        },
        (err) => {
          setFetchFields({ ...fetchFields, err: "Data Not Found" });
          // setLoading(false);
        },
      ),
    );
  };
  useEffect(() => {
    if (!isEdit || !fields?.sfgs || !fields?.skds || !fields?.rawMaterials)
      return;

    // Reset fetchFields for edit mode
    setFetchFields({ sfgs: [], skds: [], rawMaterials: [] });

    // Populate fetchFields with available materials in edit mode
    if (fields?.sfgs?.length > 0) {
      fields?.sfgs?.forEach((element) => {
        if (
          element.status === MATERIAL_STAGES.AVAILABLE ||
          element.status === MATERIAL_STAGES.RECEIVED
        ) {
          fetchList(
            moment().toISOString(),
            element?.sfgId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.SFG,
            "sfgs",
          );
        }
      });
    }

    if (fields?.skds?.length > 0) {
      fields?.skds?.forEach((element) => {
        if (
          element.status === MATERIAL_STAGES.AVAILABLE ||
          element.status === MATERIAL_STAGES.RECEIVED
        ) {
          fetchList(
            moment().toISOString(),
            element?.skdId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.SKD,
            "skds",
          );
        }
      });
    }

    if (fields?.rawMaterials?.length > 0) {
      fields?.rawMaterials?.forEach((element) => {
        if (
          element.status === MATERIAL_STAGES.AVAILABLE ||
          element.status === MATERIAL_STAGES.RECEIVED
        ) {
          fetchList(
            moment().toISOString(),
            element?.rawMaterialId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.RAW_MATERIALS,
            "rawMaterials",
          );
        }
      });
    }
  }, [fields, isEdit, totalQuantity]); // Dependencies for edit mode

  useEffect(() => {
    if (isview || isEdit || hasFetched.current) return;

    if (!fields?.sfgs && !fields?.skds && !fields?.rawMaterials) return;

    hasFetched.current = true;

    if (fields?.sfgs?.length > 0) {
      fields?.sfgs?.forEach((element) => {
        if (element.status === MATERIAL_STAGES.AVAILABLE) {
          fetchList(
            moment().toISOString(),
            element?.sfgId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.SFG,
            "sfgs",
          );
        }
      });
    }
    if (fields?.skds?.length > 0) {
      fields?.skds?.forEach((element) => {
        if (element.status === MATERIAL_STAGES.AVAILABLE) {
          fetchList(
            moment().toISOString(),
            element?.skdId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.SKD,
            "skds",
          );
        }
      });
    }
    if (fields?.rawMaterials?.length > 0) {
      fields?.rawMaterials?.forEach((element) => {
        if (element.status === MATERIAL_STAGES.AVAILABLE) {
          fetchList(
            moment().toISOString(),
            element?.rawMaterialId,
            element?.min_of_quantity * totalQuantity,
            MATERIAL_TYPE.RAW_MATERIALS,
            "rawMaterials",
          );
        }
      });
    }
  }, [fields, isEdit, isview]);

  console.log("fetchFields", fetchFields);

  useEffect(() => {
    if (id) fetchById(id);
  }, [id, fetchById]);

  const startProduction = async (e) => {
    e.preventDefault();
    setLoading(true);
    const productionData = {
      ...fetchFields,
      productionPlan: id,
    };
    console.log("Sending production data:", productionData); // Debug log

    dispatch(
      callApiAction(
        async () => await ProductionPlanningStockOutApi(productionData),
        async (response) => {
          callBack();
          setLoading(false);
          dispatch(
            callSnackBar(
              "Production Started Successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
          dispatch(closeModal("Production-Planning-Edit"));
        },
        (err) => {
          setLoading(false);
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const finishProduction = async () => {
    setLoading(true);
    dispatch(
      callApiAction(
        async () =>
          await updateProductionPlanningApi(
            { id },
            { status: PRODUCTION_PLANNING_STATUS.PRODUCTION_COMPLETED },
          ),
        async () => {
          callBack();
          setLoading(false);
          dispatch(
            callSnackBar(
              "Production Finished Successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
          dispatch(closeModal("Production-Planning-Edit"));
        },
        (err) => {
          setLoading(false);
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const convertReworkableToPass = async () => {
    const convertQty = getNumber(reworkableToPassedQty);
    const reworkableQty = getNumber(fgStatusSummary?.reworkableQty);

    if (!convertQty) {
      dispatch(
        callSnackBar(
          "Please enter quantity to convert",
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    if (convertQty < 0) {
      dispatch(
        callSnackBar("Quantity cannot be negative", SNACK_BAR_VARIETNS.error),
      );
      return;
    }

    if (convertQty > reworkableQty) {
      dispatch(
        callSnackBar(
          `Quantity exceeds available reworkable FG (${reworkableQty})`,
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    setConvertingStatus(true);
    dispatch(
      callApiAction(
        async () =>
          await UpdateAssemblyFgStatus({
            productionPlan: id,
            quantity: convertQty,
            fromStatus: 1, // REWORKABLE
            toStatus: 3, // PASSED
          }),
        async () => {
          setReworkableToPassedQty("");
          setConvertingStatus(false);
          await fetchAssemblyStockSummary(id);
          dispatch(
            callSnackBar(
              "Reworkable FG converted to Passed successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
        },
        (err) => {
          setConvertingStatus(false);
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const convertReworkableToRejected = async () => {
    const convertQty = getNumber(reworkableToRejectedQty);
    const reworkableQty = getNumber(fgStatusSummary?.reworkableQty);

    if (!convertQty) {
      dispatch(
        callSnackBar(
          "Please enter quantity to convert",
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    if (convertQty < 0) {
      dispatch(
        callSnackBar("Quantity cannot be negative", SNACK_BAR_VARIETNS.error),
      );
      return;
    }

    if (convertQty > reworkableQty) {
      dispatch(
        callSnackBar(
          `Quantity exceeds available reworkable FG (${reworkableQty})`,
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    setConvertingToRejectedStatus(true);
    dispatch(
      callApiAction(
        async () =>
          await UpdateAssemblyFgStatus({
            productionPlan: id,
            quantity: convertQty,
            fromStatus: 1, // REWORKABLE
            toStatus: 2, // REJECTED
          }),
        async () => {
          setReworkableToRejectedQty("");
          setConvertingToRejectedStatus(false);
          await fetchAssemblyStockSummary(id);
          dispatch(
            callSnackBar(
              "Reworkable FG converted to Rejected successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
        },
        (err) => {
          setConvertingToRejectedStatus(false);
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const recordProductionProgress = async () => {
    const enteredQty = getNumber(progressEntryQty);
    const { remainingFgQty } = productionProgressSummary;

    if (!enteredQty) {
      dispatch(
        callSnackBar("Please enter FG quantity", SNACK_BAR_VARIETNS.error),
      );
      return;
    }

    if (enteredQty < 0) {
      dispatch(
        callSnackBar(
          "FG quantity cannot be negative",
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    if (enteredQty > remainingFgQty) {
      dispatch(
        callSnackBar(
          `Entered quantity exceeds remaining FG (${remainingFgQty})`,
          SNACK_BAR_VARIETNS.error,
        ),
      );
      return;
    }

    if (!fgStatus) {
      dispatch(
        callSnackBar("Please select FG status", SNACK_BAR_VARIETNS.error),
      );
      return;
    }

    const payload = {
      productionPlan: id,
      fgQuantity: enteredQty,
      fgStatus: Number(fgStatus),
    };

    setRecordingProduction(true);
    dispatch(
      callApiAction(
        async () => await CreateAssemblyStockOut(payload),
        async () => {
          await fetchById(id);
          setProgressEntryQty("");
          setFgStatus("");
          setRecordingProduction(false);
          dispatch(
            callSnackBar(
              "Production quantity recorded successfully",
              SNACK_BAR_VARIETNS.suceess,
            ),
          );
        },
        (err) => {
          setRecordingProduction(false);
          dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
        },
      ),
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      if (isProductionStartedView) {
        finishProduction();
      } else {
        startProduction(e);
      }
    } else if (!isview) {
      createFunction(e);
    }
  };

  return (
    <CreateProductionPlanUi
      loading={loading}
      fields={fields}
      quantity={totalQuantity}
      onSubmit={(e) => onSubmit(e)}
      setFields={setFields}
      isview={isview}
      isEdit={isEdit}
      canStartProduction={canStartProduction}
      isProductionStartedView={isProductionStartedView}
      progressEntryQty={progressEntryQty}
      setProgressEntryQty={setProgressEntryQty}
      fgStatus={fgStatus}
      setFgStatus={setFgStatus}
      productionProgressSummary={productionProgressSummary}
      fgStatusSummary={fgStatusSummary}
      reworkableToPassedQty={reworkableToPassedQty}
      setReworkableToPassedQty={setReworkableToPassedQty}
      convertingStatus={convertingStatus}
      onConvertReworkableToPass={convertReworkableToPass}
      reworkableToRejectedQty={reworkableToRejectedQty}
      setReworkableToRejectedQty={setReworkableToRejectedQty}
      convertingToRejectedStatus={convertingToRejectedStatus}
      onConvertReworkableToRejected={convertReworkableToRejected}
      recordingProduction={recordingProduction}
      onRecordProduction={recordProductionProgress}
    />
  );
};

export default CreateProductionPlanController;
